import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  Shield, Leaf, Moon, Zap, Heart, Dumbbell, Activity, 
  HeartPulse, Coffee, ChevronRight, Sparkles, ArrowRight,
  TrendingUp, Clock, Star
} from 'lucide-react';
import { 
  wellnessCategories, 
  wellnessProducts, 
  getProductsByCategory,
  getFeaturedProducts,
  getBestSellers,
  getNewArrivals
} from '../../data/wellnessData';

const iconMap = {
  'Shield': Shield,
  'Leaf': Leaf,
  'Moon': Moon,
  'Zap': Zap,
  'Heart': Heart,
  'Dumbbell': Dumbbell,
  'Activity': Activity,
  'HeartPulse': HeartPulse,
  'Coffee': Coffee
};

const WellnessCatalogue = () => {
  const [activeCategory, setActiveCategory] = useState(null);
  const featuredProducts = getFeaturedProducts();
  const bestSellers = getBestSellers();
  const newArrivals = getNewArrivals();

  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName] || Shield;
    return IconComponent;
  };

  const displayProducts = activeCategory 
    ? getProductsByCategory(activeCategory) 
    : wellnessProducts;

  const activeCategoryData = activeCategory 
    ? wellnessCategories.find(c => c.id === activeCategory) 
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 via-white to-orange-50">
      {/* Hero Section */}
      <section 
        className="relative py-20 px-4 overflow-hidden"
        style={{
          backgroundImage: 'url(https://static.prod-images.emergentagent.com/jobs/eb4529a2-4ca6-41dd-a194-af190196c3ae/images/24de089d2fe994bf370732b0f01b55bff434c81030e30910a7c40d9be8baca51.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#263776]/80 via-[#263776]/70 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <Badge className="bg-[#F47D20] text-white mb-4">
              <Sparkles className="w-3 h-3 mr-1" /> ORIVANA Wellness
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Ayurvedic Wellness
              <span className="block text-[#F47D20]">For Modern Living</span>
            </h1>
            <p className="text-lg text-white/90 mb-6">
              Discover our curated collection of premium Ayurvedic supplements, herbal teas, 
              and wellness products. Ancient wisdom meets modern science for holistic health.
            </p>
            <div className="flex flex-wrap gap-3">
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-3 py-1">
                USDA Organic
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-3 py-1">
                Non-GMO
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-3 py-1">
                GMP Certified
              </Badge>
              <Badge variant="outline" className="bg-white/10 text-white border-white/30 px-3 py-1">
                Vegan Options
              </Badge>
            </div>
          </div>
        </div>
      </section>

      {/* Best Sellers Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-[#F47D20] p-2 rounded-lg">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#263776]">Best Sellers</h2>
                <p className="text-gray-600 text-sm">Our most loved products by customers</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {bestSellers.slice(0, 6).map((product) => {
              const category = wellnessCategories.find(c => c.id === product.category);
              return (
                <Link 
                  key={product.id} 
                  to={`/products/wellness/${product.slug}`}
                  data-testid={`bestseller-${product.slug}`}
                >
                  <Card className="group h-full overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-white">
                    <div className="relative aspect-square overflow-hidden">
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <Shield className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <Badge className="absolute top-2 left-2 bg-amber-500 text-white text-xs">
                        <Star className="w-3 h-3 mr-1" /> Best Seller
                      </Badge>
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-medium text-sm text-[#263776] group-hover:text-[#F47D20] transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{category?.name}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* New Arrivals Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-green-50 to-emerald-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-500 p-2 rounded-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-[#263776]">New Arrivals</h2>
                <p className="text-gray-600 text-sm">Fresh additions to our wellness collection</p>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {newArrivals.slice(0, 6).map((product) => {
              const category = wellnessCategories.find(c => c.id === product.category);
              return (
                <Link 
                  key={product.id} 
                  to={`/products/wellness/${product.slug}`}
                  data-testid={`newarrival-${product.slug}`}
                >
                  <Card className="group h-full overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-white">
                    <div className="relative aspect-square overflow-hidden">
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gray-100">
                          <Shield className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <Badge className="absolute top-2 left-2 bg-emerald-500 text-white text-xs">
                        <Sparkles className="w-3 h-3 mr-1" /> New
                      </Badge>
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-medium text-sm text-[#263776] group-hover:text-[#F47D20] transition-colors line-clamp-2">
                        {product.name}
                      </h3>
                      <p className="text-xs text-gray-500 mt-1">{category?.name}</p>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Shop by Health Concern */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#263776] mb-3">Shop by Health Concern</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Find the right wellness solution for your specific health goals
            </p>
          </div>

          {/* Category Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            <Button
              variant={activeCategory === null ? "default" : "outline"}
              className={`rounded-full ${activeCategory === null ? 'bg-[#263776]' : ''}`}
              onClick={() => setActiveCategory(null)}
            >
              All Products
            </Button>
            {wellnessCategories.map((category) => {
              const IconComponent = getIcon(category.icon);
              return (
                <Button
                  key={category.id}
                  variant={activeCategory === category.id ? "default" : "outline"}
                  className={`rounded-full ${activeCategory === category.id ? 'bg-[#263776]' : ''}`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {category.name}
                </Button>
              );
            })}
          </div>

          {/* Active Category Description */}
          {activeCategoryData && (
            <div 
              className="mb-8 p-6 rounded-2xl text-white text-center"
              style={{ backgroundColor: activeCategoryData.color }}
            >
              <h3 className="text-2xl font-bold mb-2">{activeCategoryData.name}</h3>
              <p className="opacity-90">{activeCategoryData.description}</p>
            </div>
          )}

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {displayProducts.map((product) => {
              const category = wellnessCategories.find(c => c.id === product.category);
              return (
                <Link 
                  key={product.id} 
                  to={`/products/wellness/${product.slug}`}
                  data-testid={`wellness-product-${product.slug}`}
                >
                  <Card className="group h-full overflow-hidden hover:shadow-xl transition-all duration-300 border-0 bg-white">
                    <div className="relative aspect-square overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
                      {product.image ? (
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Shield className="w-16 h-16 text-gray-300" />
                        </div>
                      )}
                      <div className="absolute top-3 left-3 flex flex-col gap-1">
                        {product.isBestSeller && (
                          <Badge className="bg-amber-500 text-white text-xs">
                            <Star className="w-3 h-3 mr-1" /> Best Seller
                          </Badge>
                        )}
                        {product.isNewArrival && (
                          <Badge className="bg-emerald-500 text-white text-xs">
                            <Sparkles className="w-3 h-3 mr-1" /> New
                          </Badge>
                        )}
                        {product.featured && !product.isBestSeller && !product.isNewArrival && (
                          <Badge className="bg-[#F47D20] text-white text-xs">
                            Featured
                          </Badge>
                        )}
                      </div>
                      <Badge 
                        className="absolute top-3 right-3 text-white text-xs"
                        style={{ backgroundColor: category?.color || '#263776' }}
                      >
                        {category?.name}
                      </Badge>
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-semibold text-[#263776] group-hover:text-[#F47D20] transition-colors mb-1">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                        {product.tagline}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">
                          {product.specifications?.['Pack Size']}
                        </span>
                        <ArrowRight className="w-4 h-4 text-[#F47D20] group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Category Cards Section */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-green-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#263776] mb-3">Explore by Category</h2>
            <p className="text-gray-600">Deep dive into our wellness collections</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wellnessCategories.map((category) => {
              const IconComponent = getIcon(category.icon);
              const productCount = getProductsByCategory(category.id).length;
              return (
                <Card 
                  key={category.id}
                  className="group cursor-pointer overflow-hidden hover:shadow-xl transition-all duration-300"
                  onClick={() => {
                    setActiveCategory(category.id);
                    window.scrollTo({ top: 400, behavior: 'smooth' });
                  }}
                >
                  <CardContent className="p-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${category.color}20` }}
                    >
                      <IconComponent 
                        className="w-7 h-7" 
                        style={{ color: category.color }}
                      />
                    </div>
                    <h3 className="text-xl font-semibold text-[#263776] mb-2 group-hover:text-[#F47D20] transition-colors">
                      {category.name}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      {category.description}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-400">{productCount} products</span>
                      <ChevronRight className="w-5 h-5 text-[#F47D20] group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* ORIVANA Brand Story */}
      <section className="py-16 px-4 bg-[#263776] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-[#F47D20] text-white mb-4">The ORIVANA Promise</Badge>
              <h2 className="text-3xl font-bold mb-6">
                Ancient Wisdom, <span className="text-[#F47D20]">Modern Standards</span>
              </h2>
              <p className="text-white/80 mb-6">
                ORIVANA represents our commitment to bringing authentic Ayurvedic wellness 
                to the global market. Every product is crafted with reverence for traditional 
                formulations while meeting the highest international quality standards.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#F47D20]">100%</div>
                  <div className="text-sm text-white/70">Natural Ingredients</div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#F47D20]">5000+</div>
                  <div className="text-sm text-white/70">Years of Ayurveda</div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#F47D20]">GMP</div>
                  <div className="text-sm text-white/70">Certified Facilities</div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold text-[#F47D20]">Lab</div>
                  <div className="text-sm text-white/70">Tested & Verified</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {featuredProducts.slice(0, 4).map((product) => (
                <Link 
                  key={product.id}
                  to={`/products/wellness/${product.slug}`}
                  className="group"
                >
                  <div className="bg-white rounded-xl overflow-hidden">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full aspect-square object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-[#F47D20] to-orange-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Start Your Wellness Journey?</h2>
          <p className="text-white/90 mb-8 max-w-2xl mx-auto">
            Connect with our team to learn more about ORIVANA wellness products 
            and how they can support your health goals.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#F47D20] hover:bg-gray-100">
                Request a Quote
              </Button>
            </Link>
            <Link to="/products">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                Explore All Products
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WellnessCatalogue;
